import { formatMoney, InvalidDecimalError, roundToMinorUnits } from "../../lib/money";

// The single way money reaches the screen. Input is always the API's exact
// decimal string. When rounding to minor units changed the value, the
// figure is marked "display-only" and the exact value is available on
// hover/focus and to screen readers — the ledger figure is never silently
// replaced by a rounded one (ARCHITECTURE.md §10: the live layer "rounds
// nothing"; rounding here is presentation only).

interface MoneyProps {
  value: string;
  currency: string;
  /**
   * Always show the display-only marker, even when this particular value
   * happened to round exactly (for figures UX-DESIGN.md requires labeled,
   * e.g. live/provisional totals).
   */
  alwaysFlag?: boolean;
  className?: string;
}

export function Money({ value, currency, alwaysFlag, className }: MoneyProps) {
  let formatted: string;
  let isRounded: boolean;
  try {
    formatted = formatMoney(value, currency);
    isRounded = roundToMinorUnits(value, currency).isRounded;
  } catch (err) {
    if (err instanceof InvalidDecimalError) {
      return <span className={className}>{`${value} ${currency}`}</span>;
    }
    throw err;
  }
  const flag = isRounded || alwaysFlag;
  const exactLabel = `Exact ledger value: ${value} ${currency.toUpperCase()}. Shown rounded half-even to ${currency.toUpperCase()} minor units for display only.`;
  return (
    <span className={["money", className ?? ""].filter(Boolean).join(" ")}>
      <data value={value}>{formatted}</data>
      {flag ? (
        <abbr className="money-flag" title={exactLabel} tabIndex={0} aria-label={exactLabel}>
          display
        </abbr>
      ) : null}
    </span>
  );
}

/** A labeled figure for dashboards. */
export function MoneyStat({
  label,
  value,
  currency,
  note,
  alwaysFlag,
}: {
  label: string;
  value: string;
  currency: string;
  note?: string;
  alwaysFlag?: boolean;
}) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">
        <Money value={value} currency={currency} alwaysFlag={alwaysFlag} />
      </span>
      {note ? <span className="stat-note">{note}</span> : null}
    </div>
  );
}
